import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';

export function Consent() {
  const [searchParams] = useSearchParams();
  const partnerId = searchParams.get('partnerId');
  
  const [partner, setPartner] = useState<{ id: string; name: string; email: string; gdprConsentAt: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (partnerId) {
      fetchPartner();
    } else {
      setError('Link invalid');
      setLoading(false);
    }
  }, [partnerId]);

  async function fetchPartner() {
    try {
      const res = await fetch(`${API_ENDPOINTS.consent}?partnerId=${partnerId}`);
      if (!res.ok) throw new Error('Partener negăsit');
      const data = await res.json();
      setPartner(data);
      if (data.gdprConsentAt) setConsented(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eroare');
    } finally {
      setLoading(false);
    }
  }

  async function handleConsent() {
    if (!partnerId) return;
    setSubmitting(true);
    try {
      const res = await fetch(API_ENDPOINTS.consent, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ partnerId }),
      });
      const data = await res.json();
      if (data.success) {
        setConsented(true);
      } else {
        setError(data.error || 'Eroare la salvare');
      }
    } catch (err) {
      setError('Eroare la conexiune');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal"></div>
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <h1 className="text-xl font-serif text-red-600 mb-4">Eroare</h1>
          <p className="text-muted">{error || 'Link invalid'}</p>
        </div>
      </div>
    );
  }

  if (consented) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-serif text-teal mb-4">Bine ai venit, {partner.name}!</h1>
          <p className="text-muted mb-6">
            Consimțământul GDPR a fost acordat cu succes. Link-ul tău de referral este acum activ.
          </p>
          <p className="text-sm text-muted mb-6">
            Vei primi un email cu instrucțiuni pentru a te autentifica în portal.
          </p>
          <Link 
            to="/auth/login" 
            className="inline-block px-6 py-3 bg-teal text-white font-medium rounded-lg hover:bg-teal-light transition-colors"
          >
            Autentificare
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-serif text-teal mb-2">BrainMap Partner Portal</h1>
          <p className="text-muted">Bine ai venit, {partner.name}</p>
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
          <p className="text-amber-800 text-sm">
            Pentru a activa contul și link-ul de referral, trebuie să acorzi consimțământul GDPR.
          </p>
        </div>

        <div className="prose prose-sm max-w-none mb-6">
          <h2 className="text-lg font-medium text-ink mb-4">Politica de Confidențialitate (GDPR)</h2>
          <div className="bg-warm p-4 rounded-lg text-sm space-y-3 max-h-64 overflow-y-auto">
            <p>
              <strong>1. Ce date colectăm:</strong> Numele, email-ul și numărul de telefon al clienților recomandați, precum și datele tale de contact ca partener.
            </p>
            <p>
              <strong>2. Scopul prelucrării:</strong> Gestionarea recomandărilor, calculul comisioanelor și comunicarea cu tine.
            </p>
            <p>
              <strong>3. Durata stocării:</strong> Datele sunt păstrate pe durata colaborării și conform obligațiilor legale fiscale.
            </p>
            <p>
              <strong>4. Drepturile tale:</strong> Ai dreptul de acces, rectificare, ștergere, restricționare și portabilitate a datelor.
            </p>
            <p>
              <strong>5. Contact DPO:</strong> dpo@institutulbrainmap.ro
            </p>
            <p>
              <strong>6. Transmiterea datelor:</strong> Datele clienților sunt criptate (AES-256-GCM) și nu sunt transmise terților.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 mb-6">
          <div className="flex-1">
            <p className="text-sm text-muted">
              Prin apăsarea butonului de mai jos, confirm că am citit și accept politica de confidențialitate 
              și că sunt de acord cu prelucrarea datelor personale conform Regulamentului GDPR.
            </p>
          </div>
        </div>

        <button
          onClick={handleConsent}
          disabled={submitting}
          className="w-full py-3 px-4 bg-teal text-white font-medium rounded-lg hover:bg-teal-light transition-colors disabled:opacity-50"
        >
          {submitting ? 'Se procesează...' : 'Acord Consimțământul GDPR'}
        </button>

        {error && (
          <p className="mt-4 text-red-600 text-sm text-center">{error}</p>
        )}
      </div>
    </div>
  );
}
